<template>
  <div class="list-response">
    <template v-if="responseSet.type === 'dropdown'">
      <TagField
        class="form-input--small"
        :options="responseSetOptions"
        :value="itemResponse || []"
        :hide-label="true"
        :placeholder="$t('Select from dropdown')"
        :errors="[]"
        :max-tags="item.params.multiple_selection ? undefined : 1"
        @input="onInputResponse"
      />
    </template>
    <template v-else>
      <template v-for="response in responseSet.responses">
        <ListResponse
          :key="response.uuid"
          :response="response"
          :selected="itemResponse ? itemResponse.includes(response.uuid) : false"
          @click="responseClicked"
        />
      </template>
    </template>

    <div
      v-if="item.notes_allowed !== false || item.photos_allowed !== false || item.issues_allowed !== false"
      style="height: 2rem; margin-top:0.4rem"
      class="question__options"
    >
      <CommentButton
        v-if="item.notes_allowed !== false"
        :contains-notes="!!item.notes"
        :classes="notesIconClass"
        @click="showNotes"
      />
      <PhotoButton
        v-if="item.photos_allowed !== false"
        :contains-photos="containsPhotos"
        :classes="photosIconClass"
        @click="showPhotos"
      />
      <ActionButton
        v-if="issuesAllowed"
        :contains-action="containsIssues"
        :classes="raiseIssueButtonClasses"
        :disabled="issuesDisabled"
        @click="raiseIssueButtonClicked"
      />
    </div>

    <portal to="modals">
      <transition name="fade">
        <RaiseIssueModal
          v-if="isRaiseIssueModalVisible"
          :question-id="item.uuid"
          @close="raiseIssueModalClosed"
        />
      </transition>
    </portal>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import ListResponse
    from '@/_legacy/components/ConductInspection/ConductInspectionItem/ConductInspectionItemQuestion/ConductInspectionItemQuestionListResponse.vue';
  import DropdownModal from '@/_legacy/views/ConductInspection/Modals/DropdownModal.vue';
  import RaiseIssueModal from '@/_legacy/views/ConductInspection/Modals/RaiseIssueModal.vue';
  import CameraIcon from '@/_legacy/components/Icons/CameraIcon.vue';
  import { INSPECTION_STATUS, ISSUE_PERMISSIONS } from '@/_legacy/constants';
  import authMixin from '@/_legacy/mixins/authMixin';
  import NotesIcon from '@/_legacy/components/Icons/NotesIcon';
  import isObject from 'lodash/isObject';
  import CommentButton from '@/_legacy/components/ConductInspection/buttons/CommentButton.vue';
  import PhotoButton from '@/_legacy/components/ConductInspection/buttons/PhotoButton.vue';
  import ActionButton from '@/_legacy/components/ConductInspection/buttons/ActionButton.vue';
  import { AssetsPhotoActions } from '@/services/assets-api/AssetsPhotoService';
  import { TagField } from '@/ui/components/form';
  import { useConductStore } from '@/stores/conduct/conduct';

  export default {
    components: {
      NotesIcon,
      ListResponse,
      DropdownModal,
      RaiseIssueModal,
      CameraIcon,
      CommentButton,
      PhotoButton,
      ActionButton,
      TagField,
    },

    mixins: [authMixin],

    props: {
      item: {
        type: Object,
        required: true,
      },
      responseSets: {
        type: Array,
        required: true,
      },
    },
    setup() {
      const { isTranslationEnabled } = useConductStore();

      return { isTranslationEnabled };
    },
    data: () => ({
      dropdownVisible: false,
      isRaiseIssueModalVisible: false,
    }),
    computed: {
      ...mapGetters({
        inspection: 'conduct/inspection',
      }),

      issuesAllowed() {
        return this.item.issues_allowed !== false && this.hasPermission(
          ISSUE_PERMISSIONS.CREATE_PERMISSION_NAME,
        );
      },

      responseSet() {
        for (const i in this.responseSets) {
          if (this.responseSets[i].uuid === this.item.params.response_set) {
            return this.responseSets[i];
          }
        }
        return [];
      },

      responseSetOptions() {
        return this.responseSet.responses.map(response => ({
          value: response.uuid,
          label: this.isTranslationEnabled && response.title_translated
            ? response.title_translated
            : response.title,
        }));
      },

      itemResponse() {
        // Somewhere along the way we are sending the response as object, array or string.
        // We need to check these cases here. Please, talk with Damir (Project Manager)
        // to ask if a task where each client (iOS, webapp and Android) sends
        // a key explaining where this inspection was performed.
        // @see https://github.com/lumiform/webapp/pull/157/files
        if (this.item.response) {
          // It is important that `Array.isArray` comes before
          // `isObject` since an `array` in JS is an object
          if (Array.isArray(this.item.response)) {
            return this.item.response;
          }

          if (isObject(this.item.response)) {
            return Object.values(this.item.response);
          }

          if (typeof this.item.response === 'string') {
            return [this.item.response];
          }

          return this.item.response;
        }
      },

      containsPhotos() {
        return this.item.photos?.filter(photo => photo.action !== AssetsPhotoActions.DELETE_IMAGE).length > 0;
      },

      issues() {
        return typeof this.inspection.issues === 'object'
          ? Object.values(this.inspection.issues)
          : this.inspection.issues;
      },

      raisedIssues() {
        return this.issues?.find(row => {
          const splitUuid = row.inspection_question_uuid ? row.inspection_question_uuid.split('-') : [];
          const uuid = splitUuid.slice(1, 6).join('-');

          return uuid === this.item.uuid;
        });
      },

      newIssues() {
        return typeof this.inspection.issues === 'object'
          ? Object.keys(this.inspection.issues)?.find(uuid => uuid === this.item.uuid)
          : null;
      },

      containsIssues() {
        return this.raisedIssues || this.newIssues;
      },

      issuesDisabled() {
        return this.inspection.inspection_id
          && this.inspection.status === INSPECTION_STATUS.DONE
          && this.raisedIssues;
      },

      notesIconClass() {
        if (this.item.notes) {
          return 'text-primary';
        }
        return 'text-60';
      },

      photosIconClass() {
        if (this.containsPhotos) {
          return 'text-primary';
        }
        return 'text-60';
      },

      raiseIssueButtonClasses() {
        return {
          'text-60': !this.containsIssues,
          'text-primary': this.containsIssues,
        };
      },
    },

    methods: {
      raiseIssueButtonClicked() {
        this.isRaiseIssueModalVisible = true;
      },

      raiseIssueModalClosed() {
        this.isRaiseIssueModalVisible = false;
      },

      responseClicked(value) {
        let response;

        if (Array.isArray(this.item.response)) {
          response = [this.item.response[0]];
        }

        if (isObject(this.item.response)) {
          response = Object.values(this.item.response);
        }

        if (typeof this.item.response === 'string') {
          response = [this.item.response];
        }

        if (typeof (response) === 'undefined') {
          response = [];
        }

        const index = response.indexOf(value);
        if (index > -1) {
          response.splice(index, 1);
        } else {
          if (!this.item.params.multiple_selection) {
            response = [];
          }
          response.push(value);
        }

        this.$store.commit('conduct/changeResponse', {
          item: this.item.uuid,
          response,
          responded: (response.length > 0),
        });
      },

      showNotes() {
        this.$store.commit('conduct/showNotesModal', this.item);
      },

      showPhotos() {
        this.$store.commit('conduct/showPhotosModal', this.item);
      },

      onInputResponse(response) {
        this.$store.commit('conduct/changeResponse', {
          item: this.item.uuid,
          response,
          responded: (response.length > 0),
        });
      },
    },
  };
</script>
